<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="rightyStore.isDrawerOpen"
        :class="[
          'fixed z-[1200] flex flex-col overflow-hidden border border-slate-700 bg-white shadow-2xl dark:bg-slate-900',
          isMobile
            ? 'inset-0'
            : 'glow-window bottom-20 right-6 h-[600px] w-[420px] rounded-2xl'
        ]"
      >
        <!-- Header -->
        <div class="flex items-center justify-between bg-slate-800 px-4 py-3">
          <div class="flex items-center gap-2">
            <div class="h-8 w-8 overflow-hidden rounded-full bg-slate-700 shadow p-0">
              <img
                src="/img/righty-light.jpg"
                alt="Righty"
                class="h-full w-full rounded-full object-cover dark:hidden"
              >
              <img
                src="/img/righty-dark.jpg"
                alt="Righty"
                class="hidden h-full w-full rounded-full object-cover dark:block"
              >
            </div>
            <span class="font-semibold text-white">Righty</span>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="hasMessagesToShow"
              class="rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              title="New conversation"
              @click="handleNewThread"
            >
              <DocumentIcon class="h-5 w-5" />
            </button>
            <button
              :class="[
                'transition-colors',
                isMobile
                  ? 'flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30'
                  : 'rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white'
              ]"
              @click="rightyStore.closeDrawer"
              aria-label="Close Righty"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>
        </div>

        <!-- Content Area -->
        <div class="flex flex-1 flex-col overflow-hidden bg-white dark:bg-slate-900">
          <!-- Ready to Chat State (suggestions UI - show when no thread and no synthetic message) -->
          <div
            v-if="!hasMessagesToShow"
            class="flex flex-1 flex-col p-4"
          >
            <div class="flex flex-1 flex-col items-center justify-center">
              <div class="mb-4 h-16 w-16 overflow-hidden rounded-full bg-slate-200 p-1.5 shadow-lg dark:bg-slate-700">
                <img
                  src="/img/righty-light.jpg"
                  alt="Righty"
                  class="h-full w-full rounded-full object-cover dark:hidden"
                >
                <img
                  src="/img/righty-dark.jpg"
                  alt="Righty"
                  class="hidden h-full w-full rounded-full object-cover dark:block"
                >
              </div>
              <p class="mb-6 text-sm text-slate-500 dark:text-slate-400">
                How can Righty help?
              </p>
            </div>
            <div class="space-y-2">
              <p class="mb-3 text-center text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Ideas:
              </p>
              <button
                v-for="prompt in rightyStore.contextualSuggestions"
                :key="prompt.label"
                class="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 shadow-sm transition-all hover:border-indigo-300 hover:bg-indigo-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-indigo-500 dark:hover:bg-slate-700"
                @click="sendSuggestion(prompt)"
              >
                {{ prompt.drawerLabel || prompt.label }}
              </button>
            </div>
          </div>

          <!-- Messages -->
          <div
            v-else
            ref="messagesContainer"
            class="flex-1 overflow-y-auto p-4"
          >
            <div class="space-y-4">
              <RightyMessage
                v-for="message in displayMessages"
                :key="message.id"
                :message="message"
                :changelog="rightyStore.getMessageChangelog(message.id)"
                :is-latest-changelog="latestChangelogMessageId === message.id"
                :is-undone="undoneMessageIds.has(message.id)"
                @undo="handleUndo"
              />

              <!-- Streaming AI response section -->
              <template v-if="rightyStore.isStreaming">
                <!-- SINGLE-PHASE: Interleaved segments (Ferndesk-style) -->
                <template v-if="rightyStore.streamingSegments.length > 0">
                  <div class="flex items-start gap-3">
                    <div class="h-8 w-8 shrink-0 overflow-hidden rounded-full bg-slate-200 p-0.5 dark:bg-slate-700">
                      <img
                        src="/img/righty-light.jpg"
                        alt="Righty"
                        class="h-full w-full rounded-full object-cover dark:hidden"
                      >
                      <img
                        src="/img/righty-dark.jpg"
                        alt="Righty"
                        class="hidden h-full w-full rounded-full object-cover dark:block"
                      >
                    </div>
                    <div class="max-w-[85%] rounded-2xl rounded-tl-none border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                      <template
                        v-for="(seg, segIdx) in rightyStore.streamingSegments"
                        :key="segIdx"
                      >
                        <!-- Text segment -->
                        <div
                          v-if="seg.type === 'text' && seg.content?.trim()"
                          class="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-headings:my-3"
                          v-html="renderStreamingSegment(seg.content)"
                        />
                        <!-- Tool call indicator (inline) -->
                        <RightyInlineToolCall
                          v-else-if="seg.type === 'tool' && seg.toolName"
                          :tool-name="seg.toolName"
                          :status="seg.toolStatus || 'running'"
                          :tool-arguments="seg.toolArguments"
                          :tool-error="seg.toolError"
                        />
                        <!-- Interactive question prompt -->
                        <RightyQuestionPrompt
                          v-else-if="seg.type === 'question'"
                          :question="seg.question"
                          :options="seg.options"
                          :allow-custom="seg.allowCustom"
                          :selected-option="seg.selectedOption"
                          @select="handleQuestionAnswer"
                        />
                      </template>
                      <!-- Streaming cursor -->
                      <span
                        v-if="!isStreamingCodeBlock"
                        class="ml-1 inline-block h-4 w-0.5 animate-pulse bg-indigo-500 align-middle"
                      />
                    </div>
                  </div>
                </template>

                <!-- Fallback: Thinking indicator (no segments yet and not researching) -->
                <div
                  v-else-if="!rightyStore.isResearching"
                  class="flex items-start gap-3"
                >
                  <div class="h-8 w-8 shrink-0 overflow-hidden rounded-full bg-slate-200 p-0.5 dark:bg-slate-700">
                    <img
                      src="/img/righty-light.jpg"
                      alt="Righty"
                      class="h-full w-full rounded-full object-cover dark:hidden"
                    >
                    <img
                      src="/img/righty-dark.jpg"
                      alt="Righty"
                      class="hidden h-full w-full rounded-full object-cover dark:block"
                    >
                  </div>
                  <div class="flex min-h-[2rem] items-center rounded-2xl rounded-tl-none bg-slate-100 px-4 py-2 dark:bg-slate-800">
                    <RightyThinkingIndicator />
                  </div>
                </div>

                <!-- Working indicator (shown when researching/processing tool calls) -->
                <div
                  v-if="rightyStore.isResearching"
                  class="flex items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-800 dark:bg-indigo-900/30"
                >
                  <div class="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600 dark:border-indigo-700 dark:border-t-indigo-400" />
                  <span class="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                    Working on it...
                  </span>
                </div>

                <!-- Live thinking timer (shown during streaming) -->
                <div
                  v-if="elapsedThinkingTime > 0"
                  class="flex items-center justify-center gap-1.5 text-xs text-slate-400 dark:text-slate-500"
                >
                  <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12,6 12,12 16,14" />
                  </svg>
                  <span>Thinking for {{ elapsedThinkingTime }}s</span>
                </div>
              </template>

              <!-- Build Card (when action is pending) -->
              <RightyBuildCard
                v-if="rightyStore.pendingAction && !rightyStore.isStreaming"
                :action="rightyStore.pendingAction"
                @build="handleBuild"
                @dismiss="rightyStore.clearPendingAction"
              />

            </div>
          </div>
        </div>

        <!-- Input Area -->
        <div class="border-t border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
          <RightyInput
            ref="inputRef"
            :disabled="rightyStore.isStreaming"
            @send="handleSend"
          />
        </div>
      </div>
    </Transition>

    <!-- Action Preview Modal -->
    <RightyActionPreview
      v-if="showActionPreview && actionToPreview"
      :action="actionToPreview"
      @approve="handleActionApprove"
      @cancel="showActionPreview = false"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { XMarkIcon, DocumentIcon } from '@heroicons/vue/24/outline'
import { marked } from 'marked'
import { useMediaQuery } from '@vueuse/core'
import { useRightyStore } from '../stores/rightyStore'
import RightyMessage from './RightyMessage.vue'
import RightyInput from './RightyInput.vue'
import RightyActionPreview from './RightyActionPreview.vue'
import RightyBuildCard from './RightyBuildCard.vue'
import RightyThinkingIndicator from './RightyThinkingIndicator.vue'
import RightyInlineToolCall from './RightyInlineToolCall.vue'
import RightyQuestionPrompt from './RightyQuestionPrompt.vue'
import type { RightyAction, RightyPrompt } from '../types'
import { useCitationLinks } from '../composables/useCitationLinks'
import { useResourceInfo } from '../composables/useResourceInfo'
import { useRightyConfigMutationSync } from '../composables/useRightyConfigMutationSync'
import { useNotificationStore } from '@/stores/notificationStore'

const rightyStore = useRightyStore()

// Mobile detection (screens smaller than md breakpoint - 768px)
const isMobile = useMediaQuery('(max-width: 767px)')
const notificationStore = useNotificationStore()
const { interpolatePrompt } = useResourceInfo()
const { transformCitationLinks } = useCitationLinks()

// Detect if streaming content contains code blocks (config, json, javascript)
const isStreamingCodeBlock = computed(() => {
  if (!rightyStore.streamingContent) return false
  // Check if there's an unclosed code block (opened but not closed)
  const codeBlockPattern = /```(config|json|javascript|js)\b/g
  const matches = rightyStore.streamingContent.match(codeBlockPattern) || []
  const closingMatches = rightyStore.streamingContent.match(/```\s*$/gm) || []
  // If there are more opening blocks than closing blocks, we're in a code block
  return matches.length > closingMatches.length
})

/** Render a single text segment from the interleaved stream as markdown. */
function renderStreamingSegment(text: string | undefined): string {
  if (!text) return ''
  let content = transformCitationLinks(text)
  content = content.replace(
    /^Plan:\s*(.+)$/m,
    '<span class="righty-plan-line">$1</span>'
  )
  return marked(content, { async: false, breaks: true }) as string
}

const messagesContainer = ref<HTMLElement | null>(null)
const inputRef = ref<InstanceType<typeof RightyInput> | null>(null)

const showActionPreview = ref(false)
const actionToPreview = ref<RightyAction | null>(null)

// Track which messages have been undone
const undoneMessageIds = ref<Set<number>>(new Set())

// Find the most recent message that has a changelog (for showing undo button only on latest)
const latestChangelogMessageId = computed<number | null>(() => {
  const msgs = displayMessages.value
  for (let i = msgs.length - 1; i >= 0; i--) {
    const msg = msgs[i]
    if (msg.role !== 'assistant') continue
    const hasBackendChangelog = (msg.metadata?.planApplied?.changelog?.length ?? 0) > 0
    const hasFrontendChangelog = (rightyStore.getMessageChangelog(msg.id)?.length ?? 0) > 0
    if (hasBackendChangelog || hasFrontendChangelog) return msg.id
  }
  return null
})

// Live thinking timer
const elapsedThinkingTime = ref(0)
let thinkingTimerInterval: ReturnType<typeof setInterval> | null = null

function startThinkingTimer() {
  // Initialize based on streamingStartTime if available
  if (rightyStore.streamingStartTime) {
    elapsedThinkingTime.value = Math.floor((Date.now() - rightyStore.streamingStartTime) / 1000)
  } else {
    elapsedThinkingTime.value = 0
  }

  // Clear any existing interval
  if (thinkingTimerInterval) {
    clearInterval(thinkingTimerInterval)
  }

  // Update every second
  thinkingTimerInterval = setInterval(() => {
    if (rightyStore.streamingStartTime) {
      elapsedThinkingTime.value = Math.floor((Date.now() - rightyStore.streamingStartTime) / 1000)
    } else {
      elapsedThinkingTime.value++
    }
  }, 1000)
}

function stopThinkingTimer() {
  if (thinkingTimerInterval) {
    clearInterval(thinkingTimerInterval)
    thinkingTimerInterval = null
  }
  elapsedThinkingTime.value = 0
}

// Computed to merge synthetic welcome message with real messages
const displayMessages = computed(() => {
  const msgs = [...rightyStore.messages]

  // Prepend synthetic welcome message if active and no real messages yet
  if (rightyStore.isEmptyAssistantActive &&
      rightyStore.syntheticWelcomeMessage &&
      msgs.length === 0) {
    return [rightyStore.syntheticWelcomeMessage]
  }

  // Filter out empty assistant messages (created by failed streaming attempts in the cascade)
  return msgs.filter((m) => m.role !== 'assistant' || (m.content && m.content.trim().length > 0))
})

// Check if we should show the messages view (has messages OR has synthetic welcome)
const hasMessagesToShow = computed(() => {
  return displayMessages.value.length > 0
})

async function handleSend(message: string) {
  await rightyStore.sendMessage(message)
  scrollToBottom()
}

async function handleQuestionAnswer(answer: string) {
  await rightyStore.answerQuestion(answer)
  scrollToBottom()
}

function sendSuggestion(prompt: RightyPrompt) {
  const finalPrompt = interpolatePrompt(prompt.prompt)
  handleSend(finalPrompt)
}

function handleBuild() {
  if (rightyStore.pendingAction) {
    actionToPreview.value = rightyStore.pendingAction
    showActionPreview.value = true
  }
}

function handleActionApprove(createdIds: Record<string, string>) {
  showActionPreview.value = false
  actionToPreview.value = null
  rightyStore.clearPendingAction()
}

async function handleNewThread() {
  // Delete the current thread to start fresh
  if (rightyStore.currentThread) {
    await rightyStore.deleteCurrentThread()
  }
  // Focus the input
  nextTick(() => {
    inputRef.value?.focus()
  })
}

/**
 * Handle undo — calls the backend ConfigVersionService to restore previous config snapshot.
 * All plans are now backend-applied, so undo always goes through the backend API.
 */
async function handleUndo(messageId: number) {
  const success = await rightyStore.undoLastBuild()
  if (success) {
    notificationStore.addNotification('All changes have been reverted')
    undoneMessageIds.value.add(messageId)
    rightyStore.builtMessageChangelogs.delete(messageId)
    // Explicitly refetch config from backend to sync Pinia stores.
    // The WebSocket DraftConfigUpdated broadcast may not arrive in time
    // or may be dropped, so we force a refetch to ensure the UI updates.
    // Route through the coordinator so any local edits made during the
    // Righty action aren't silently discarded by wholesale adoption — if
    // the user had unsaved edits, they get the standard reload prompt
    // instead of a quiet clobber. (ENG-1066 follow-up.)
    const configStore = useConfigStore()
    configStore.requestRemoteRefetch()
  } else {
    notificationStore.addError('Failed to undo changes')
  }
}

useRightyConfigMutationSync({
  rightyStore,
  refetchConfig: () => {
    // Coordinator-routed: the `isStreaming` guard in `debouncedUpdateConfig`
    // blocks autosaves while Righty streams, so any edits typed during a
    // stream leave the local config dirty when streaming ends. A bare
    // `configQuery.refetch()` would adopt server/MCP state wholesale and
    // silently drop those edits; `requestRemoteRefetch` defers and prompts
    // instead. (ENG-1066 follow-up.)
    const configStore = useConfigStore()
    configStore.requestRemoteRefetch()
  }
})

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

// Auto-scroll on new messages
watch(
  () => rightyStore.messages.length,
  () => scrollToBottom()
)

// Auto-scroll when streaming content updates
watch(
  () => rightyStore.streamingContent,
  () => scrollToBottom()
)

// Auto-scroll when streaming segments update (single-phase interleaved mode)
watch(
  () => rightyStore.streamingSegments.length,
  () => scrollToBottom()
)

// Focus input when drawer opens
watch(
  () => rightyStore.isDrawerOpen,
  (isOpen) => {
    if (isOpen) {
      nextTick(() => {
        inputRef.value?.focus()
      })
    }
  }
)

// Start/stop thinking timer based on streaming state
watch(
  () => rightyStore.isStreaming,
  (isStreaming) => {
    if (isStreaming) {
      startThinkingTimer()
    } else {
      stopThinkingTimer()
    }
  },
  { immediate: true }
)

// Cleanup timer on unmount
onUnmounted(() => {
  stopThinkingTimer()
})
</script>

<style scoped>
.glow-window::before {
  content: '';
  background-image: linear-gradient(
    45deg,
    #d6d1d1,
    #f2e685,
    #fff,
    #e494bf,
    #63deb9,
    #fb9ccf,
    #458edf,
    #a96de5,
    #6ab2dc,
    #e894c1,
    #fff,
    #d6d1d1
  );
  position: absolute;
  top: -3px;
  left: -3px;
  background-size: 400%;
  z-index: -1;
  filter: blur(8px);
  width: calc(100% + 6px);
  height: calc(100% + 6px);
  animation: glowing 20s linear infinite;
  opacity: 0.5;
  border-radius: 1rem;
}

@keyframes glowing {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 400% 0;
  }
}

:deep(.righty-plan-line) {
  display: block;
  font-size: 0.8rem;
  font-style: italic;
  color: rgb(99 102 241); /* indigo-500 */
  margin-top: 0.25rem;
}

:global(.dark) :deep(.righty-plan-line) {
  color: rgb(165 180 252); /* indigo-300 */
}
</style>
